import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Props = {
  mode: "create" | "rename";
  initialName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Return a Promise so the dialog can await the mutation before closing. */
  onSubmit: (name: string) => void | Promise<void>;
  /** Copy overrides so the same dialog can rename videos too (added 2026-07-18).
   *  Default noun = "folder" for backward compatibility with existing folder callers. */
  noun?: "folder" | "asset";
};

/** Shared by "New Folder", folder rename, AND video rename. Async-aware. */
export function FolderNameDialog({
  mode,
  initialName = "",
  open,
  onOpenChange,
  onSubmit,
  noun = "folder",
}: Props) {
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(mode === "rename" ? initialName : "");
      setError(null);
      setBusy(false);
    }
  }, [open, mode, initialName]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onOpenChange(false);
    } catch (err) {
      setError((err as Error)?.message || `Couldn't save that ${noun} name.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {mode === "create"
                ? noun === "asset" ? "New asset" : "New folder"
                : noun === "asset" ? "Rename asset" : "Rename folder"}
            </DialogTitle>
          </DialogHeader>

          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={noun === "asset" ? "Untitled asset" : "Untitled Folder"}
            disabled={busy}
          />

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || busy}>
              {busy ? "Saving…" : mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
